var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef } from 'react';
import _ from 'lodash';
import { getInitConfig } from 'ziphy-web-shared';
import { apiService, eventsService, notificationsService } from 'ziphy-web-shared/basic/api';
import { prepareResponseData } from 'ziphy-web-shared/basic/api/myack/api.helpers';
import { $auth } from 'ziphy-web-shared/basic/entities/auth';
import { $loader, globalLoaderHandlers, WebSocketPollingListener, } from 'ziphy-web-shared/basic/utils';
export const useEventsListener = ({ isEnabled }) => {
    const listener = useRef(null);
    const buffer = useRef(new Map());
    const lastEventId = useRef(0);
    const triggerDebounced = useRef(_.debounce(() => {
        buffer.current.forEach((messages, appointmentId) => {
            const eventTypes = new Set(messages.map((x) => x.event.event));
            $loader.simpleTrigger(globalLoaderHandlers.EVENT, {
                appointmentId,
                eventTypes,
                isChatMessage: Array.from(eventTypes).every((x) => x === 'chat_message_received'),
                events: messages.map((x) => x.event),
                notifications: messages.map((x) => x.notifications).flat(),
            });
            const eventId = Math.max(...messages.map((x) => x.event.id));
            if (eventId > lastEventId.current)
                lastEventId.current = eventId;
        });
        buffer.current.clear();
    }, 300)).current;
    useEffect(() => {
        if (!listener.current) {
            listener.current = new WebSocketPollingListener({
                wsUrl: `${getInitConfig().apiService.urls.hooksUrl}/events/listen`,
                getToken: apiService.getAccessToken,
                onEvent: (message) => {
                    var _a;
                    message = prepareResponseData(message);
                    const appointmentId = (_a = message.event) === null || _a === void 0 ? void 0 : _a.appointmentId;
                    if (!buffer.current.has(appointmentId))
                        buffer.current.set(appointmentId, []);
                    buffer.current.get(appointmentId).push(message);
                    triggerDebounced();
                },
                onPolling: () => __awaiter(void 0, void 0, void 0, function* () {
                    var _a, _b, _c, _d, _e;
                    const response = lastEventId.current === 0
                        ? yield eventsService.list({
                            order: [['id', 'desc']],
                            limit: 1,
                        })
                        : yield eventsService.list({
                            filter: { gt: ['id', lastEventId.current] },
                            limit: 1000,
                        });
                    const events = ((_b = (_a = response.prepared) === null || _a === void 0 ? void 0 : _a.events) === null || _b === void 0 ? void 0 : _b.items) || [];
                    if (events === null || events === void 0 ? void 0 : events.length) {
                        const eventIds = [];
                        let notificationsMap = new Map();
                        let hasNotification = false;
                        events.forEach((x) => {
                            eventIds.push(x.id);
                            if (!hasNotification && x.recipientIds.includes($auth.currentRoleId)) {
                                hasNotification = true;
                            }
                        });
                        if (hasNotification) {
                            const res = yield notificationsService.list({
                                filter: { in: ['event_id', eventIds] },
                            });
                            (_e = (_d = (_c = res.prepared) === null || _c === void 0 ? void 0 : _c.notifications) === null || _d === void 0 ? void 0 : _d.items) === null || _e === void 0 ? void 0 : _e.forEach((x) => {
                                if (!notificationsMap.has(x.eventId))
                                    notificationsMap.set(x.eventId, []);
                                notificationsMap.get(x.eventId).push(x);
                            });
                        }
                        events.forEach((event) => {
                            const appointmentId = event.appointmentId;
                            if (!buffer.current.has(appointmentId))
                                buffer.current.set(appointmentId, []);
                            buffer.current
                                .get(appointmentId)
                                .push({ event, notifications: notificationsMap.get(event.id) || [] });
                        });
                        triggerDebounced();
                    }
                }),
            });
        }
        if (isEnabled) {
            listener.current.start();
        }
        else {
            listener.current.stop();
        }
        return () => {
            var _a;
            (_a = listener.current) === null || _a === void 0 ? void 0 : _a.stop();
            triggerDebounced.cancel();
        };
    }, [isEnabled, triggerDebounced]);
};
